<template>
  <div class="bulletin-container">
    <h1>
      Feladatok, információk

      <a-icon
          v-if="loading"
          type='loading'
      />
    </h1>

    <div class="bulletin-news-categories">
      <a-button
          type="default"
          @click="selectedCategoryIds = []; search = undefined;"
          :disabled="selectedCategoryIds.length === 0 && (search === undefined || search.length === 0)"
      >
        Összes
      </a-button>

      <div class="bulletin-news-categories-buttons">
        <a-button
            v-for="category in categories"
            :key="category.id"
            :type="selectedCategoryIds.indexOf(category.id) !== -1 ? 'primary' : 'default'"
            @click="toggleCategory(category.id)"
        >
          {{ category.name }}
        </a-button>
      </div>
    </div>
    <div class="bulletin-news-search">
      <a-input v-model="search" placeholder="Keresés" />
    </div>

    <div class="bulletin-news-container">
      <article v-for="article in news" :key="article.id"
               :class="{
        'mandatory': article.mandatory,
        'featured': isFeatured(article)
      }">
        <div v-if="isFeatured(article)" class="article-featured">
          <a-icon type="warning" />
          Határidős feladat
        </div>

        <div class="article-container">
          <div class="article-image">
            <img v-if="article.picture" :src="`/api/v1/companies/1/images/${article.picture.id}/view?width=640`"
                 :alt="article.title" />
          </div>

          <div class="article-content">
            <div class="article-info">
              <h2>{{ article.title }}</h2>

              <div class="article-categories">
                <a-tag
                    v-for="category in article.categories" :key="category.id">
                  {{ category.name }}
                </a-tag>
              </div>

              <div>Közzétéve: {{ moment(article.date).format('YYYY.MM.DD').toString() }}</div>

              <div v-if="article.deadlineDate">
                Határidő:
                <strong>{{ moment(article.deadlineDate).format('YYYY.MM.DD').toString() }}</strong>
              </div>

              <div v-if="article.mandatory" class="mandatory">
                <a-tag color="red">Kötelező feladat</a-tag>
              </div>
            </div>

            <div class="article-buttons">
              <router-link :to="`/neta/bulletin/${article.slug}`">
                <a-button type="primary">
                  Tovább
                </a-button>
              </router-link>
            </div>
          </div>
        </div>
      </article>
    </div>

    <div v-if="total > limit" class="pagination">
      <a-pagination v-model="current" :total="total" :page-size="limit" show-less-items />
    </div>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";

export default {
  computed: {
    moment() {
      return moment
    },
    offset: function () {
      return (this.current - 1) * this.limit;
    }
  },
  mounted() {
    this.fetchNews();
    this.fetchCategories();
  },
  data() {
    return {
      loading: false,
      news: [],
      categories: [],
      selectedCategoryIds: [],
      search: undefined,
      current: 1,
      limit: 12,
      total: 0
    }
  },
  watch: {
    selectedCategoryIds() {
      this.fetchNews()
    },
    search() {
      this.fetchNews()
    },
    current() {
      this.fetchNews()
    }
  },
  methods: {
    isFeatured(article) {
      const today = new Date().toISOString().split('T')[0];
      const limit = new Date(today).getTime();
      const deadlineDate = new Date(article.deadlineDate).getTime();

      return article.featured && deadlineDate >= limit;
    },
    toggleCategory(categoryId) {
      const index = this.selectedCategoryIds.indexOf(categoryId);

      if (index === -1) {
        this.selectedCategoryIds.push(categoryId);

        return;
      }

      this.selectedCategoryIds.splice(index, 1);
    },
    fetchNews() {
      this.loading = true;
      return new Promise((resolve) => {
        axios.get(
            '/api/v1/companies/1/bulletin/news/read', {
              params: {
                envelope: true,
                search: this.search && this.search.length > 0 ? '*' + this.search + '*' : undefined,
                filter: {
                  'status': 1,
                  'categories/id': this.selectedCategoryIds.join(','),
                },
                limit: this.limit,
                offset: this.offset,
                order: 'featured DESC,date DESC'
              }
            }
        ).then((response) => {
          this.total = parseInt(response.data.meta.queryRecords);

          this.news = response.data.content;

          resolve(this.news);

          this.loading = false;
        }).catch(() => {
          this.loading = true;
          this.$message.error(
              'A hírek betöltése sikertelen!'
          );
        });
      });
    },
    fetchCategories() {
      return new Promise((resolve) => {
        axios.get(
            '/api/v1/companies/1/bulletin/categories', {
              params: {
                order: 'weight ASC'
              }
            }
        ).then((response) => {
          this.categories = response.data;

          resolve(this.categories);
        }).catch(() => {
          this.$message.error(
              'A kategóriák betöltése sikertelen!'
          );
        });
      });
    },
  }
}
</script>

<style lang="scss" scoped>
h1 {
  font-size: 2em;
}

.bulletin-container {
  padding: 25px;
  max-width: 1024px;
  margin: auto;
}

.bulletin-news-categories {
  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: space-between;
}

.bulletin-news-search {
  margin: 8px 0;
}

article {
  background: #FFF;
  border: 1px solid #EFEFEF;
  border-radius: 4px;
  overflow: hidden;
  margin: 12px 0;
}

article .article-featured {
  background: #38BA9B;
  font-weight: bold;
  color: white;
  padding: 4px 8px;
}

article .article-container {
  display: flex;
  gap: 4px;
}

article.featured {
  border-color: #38BA9B;
}

article .article-content {
  padding: 8px;
  display: flex;
  flex-flow: column nowrap;
  width: 70%;
}

article .article-info {
  flex: 1;
}

article .article-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 4px;
}

article .article-categories {
  margin-bottom: 8px;
  color: #222;
}

article .article-image {
  overflow: hidden;
  position: relative;
  width: 30%;
}

article .article-image img {
  object-fit: cover;
  width: 100%;
  height: 100%;
}

.pagination {
  margin-top: 16px;
  display: flex;
  justify-content: center;
}

.mandatory {
  margin-top: 8px;
}
</style>